// src/Components/ImportMarkers/CheckBatches.js

import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import {
  MenuAddNewGrey,
  MenuSaveGrey,
  MenuDeleteGrey,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';

import { MenuImportGrey, MenuUploadGrey } from '../../assets/icons/CustomIconsAdd2';

import { MenuCreateBatch, MenuCreateBatchGrey } from '../../assets/icons/CustomIconsAdd4';

import CheckBatchesView from './CheckBatchesView';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import { collection, onSnapshot, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

// Import Image
import AdminBg from '../../assets/images/AdminBackground.png';

const ICON_SIZE = 64;

const PAGE1_UPLOAD_SCALE = 4.9;
const PAGE1_UPLOAD_ICON_X = 25;
const PAGE1_UPLOAD_ICON_Y = 38;
const PAGE1_UPLOAD_TEXT_SIZE = '18px';
const PAGE1_UPLOAD_TEXT_Y = 108;

const PAGE2_IMPORT_SCALE = 7.5;
const PAGE2_IMPORT_ICON_X = 25;
const PAGE2_IMPORT_ICON_Y = 37;
const PAGE2_IMPORT_TEXT_SIZE = '18px';
const PAGE2_IMPORT_TEXT_Y = 108;

const formatDateOnly = (isoString) => {
  if (!isoString) return 'Unknown Date';
  const d = new Date(isoString);
  return d.toLocaleDateString();
};

const formatTimeOnly = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

export default function CheckBatches() {
  const [activeBatches, setActiveBatches] = useState([]);
  const [archivedBatches, setArchivedBatches] = useState([]);
  const [markerGroups, setMarkerGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);

  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingArchived, setLoadingArchived] = useState(true);
  const [loadingMarkers, setLoadingMarkers] = useState(true);

  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedMarkerIds, setSelectedMarkerIds] = useState([]);

  const [showBatchFilenameDialog, setShowBatchFilenameDialog] = useState(false);
  const [showCreateBatchConfirm, setShowCreateBatchConfirm] = useState(false);
  const [tempBatchFilename, setTempBatchFilename] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    const unsubImports = onSnapshot(
      collection(db, 'excelImports'),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setActiveBatches(data);
        setLoadingActive(false);
      },
      (error) => {
        console.error('Error fetching excelImports in CheckBatches:', error);
        setLoadingActive(false);
      },
    );

    const unsubArchives = onSnapshot(
      collection(db, 'excelArchives'),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setArchivedBatches(data);
        setLoadingArchived(false);
      },
      (error) => {
        console.error('Error fetching excelArchives in CheckBatches:', error);
        setLoadingArchived(false);
      },
    );

    const unsubMarkerGroups = onSnapshot(
      collection(db, 'markerGroups'),
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMarkerGroups(data);
        setLoadingMarkers(false);
      },
      (error) => {
        console.error('Error fetching markerGroups in CheckBatches:', error);
        setLoadingMarkers(false);
      },
    );

    const unsubCategories = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedCats = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        fetchedCats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(fetchedCats);
      },
      (error) => console.error('Error fetching categories in CheckBatches:', error),
    );

    const unsubRegions = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        const fetchedRegions = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setRegions(fetchedRegions);
      },
      (error) => console.error('Error fetching regions in CheckBatches:', error),
    );

    return () => {
      unsubImports();
      unsubArchives();
      unsubMarkerGroups();
      unsubCategories();
      unsubRegions();
    };
  }, []);

  const allBatches = useMemo(() => {
    const combined = [...activeBatches, ...archivedBatches];
    const uniqueMap = new Map();
    combined.forEach((b) => {
      if (!uniqueMap.has(b.id)) {
        uniqueMap.set(b.id, b);
      }
    });
    const list = Array.from(uniqueMap.values());
    list.sort((a, b) => new Date(b.importDate || 0) - new Date(a.importDate || 0));
    return list;
  }, [activeBatches, archivedBatches]);

  const filteredMarkers = useMemo(() => {
    if (!selectedBatchId) return [];

    const matched = markerGroups.filter((mg) => {
      const batchId = mg.importBatchId ? String(mg.importBatchId).trim() : '';
      if (selectedBatchId === 'NONE') {
        return !batchId;
      }
      return batchId === selectedBatchId;
    });

    matched.sort((a, b) => {
      const titleA = (a.title || a.companyName || a.address || '').toLowerCase();
      const titleB = (b.title || b.companyName || b.address || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

    return matched;
  }, [markerGroups, selectedBatchId]);

  const handleBatchChange = (e) => {
    setSelectedBatchId(e.target.value);
    setSelectedMarkerIds([]);
  };

  const handleCreateBatchClick = () => {
    if (selectedMarkerIds.length === 0 || isProcessing) return;
    setTempBatchFilename('');
    setShowBatchFilenameDialog(true);
  };

  const handleFilenameDialogConfirm = (filenameInput) => {
    const cleanFilename = (filenameInput || '').trim() || 'Untitled Batch';
    setTempBatchFilename(cleanFilename);
    setShowBatchFilenameDialog(false);
    setShowCreateBatchConfirm(true);
  };

  const executeCreateBatch = async () => {
    setShowCreateBatchConfirm(false);
    const cleanFilename = (tempBatchFilename || '').trim() || 'Untitled Batch';

    if (selectedMarkerIds.length === 0) return;
    setIsProcessing(true);

    try {
      const newBatchId = crypto.randomUUID ? crypto.randomUUID() : `batch_${Date.now()}`;
      const todayDate = new Date().toISOString().substring(0, 10);

      const selectedGroupDocs = markerGroups.filter((mg) =>
        selectedMarkerIds.includes(mg.id),
      );

      // Collect all Firestore operations
      const operations = [];

      selectedGroupDocs.forEach((mg) => {
        const needsDate =
          !mg.date || (typeof mg.date === 'string' && mg.date.trim() === '');

        const groupUpdate = { importBatchId: newBatchId };
        if (needsDate) groupUpdate.date = todayDate;

        operations.push({
          type: 'update',
          ref: doc(db, 'markerGroups', mg.id),
          data: groupUpdate,
        });

        if (mg.markerID) {
          const markerUpdate = { importBatchId: newBatchId };
          if (needsDate) markerUpdate.date = todayDate;
          operations.push({
            type: 'setMerge',
            ref: doc(db, 'markers', mg.markerID),
            data: markerUpdate,
          });
        }

        if (mg.polygonID) {
          const polygonUpdate = { importBatchId: newBatchId };
          if (needsDate) polygonUpdate.date = todayDate;
          operations.push({
            type: 'setMerge',
            ref: doc(db, 'polygons', mg.polygonID),
            data: polygonUpdate,
          });
        }

        if (mg.polylineID) {
          const polylineUpdate = { importBatchId: newBatchId };
          if (needsDate) polylineUpdate.date = todayDate;
          operations.push({
            type: 'setMerge',
            ref: doc(db, 'polylines', mg.polylineID),
            data: polylineUpdate,
          });
        }
      });

      // If markers were moved out of an existing batch, update that batch's markerCount
      if (selectedBatchId && selectedBatchId !== 'NONE') {
        const activeOrigin = activeBatches.find((b) => b.id === selectedBatchId);
        const archivedOrigin = archivedBatches.find((b) => b.id === selectedBatchId);
        const originCollection = activeOrigin
          ? 'excelImports'
          : archivedOrigin
            ? 'excelArchives'
            : null;
        const originRecord = activeOrigin || archivedOrigin;

        if (originCollection && originRecord) {
          const remainingCount = Math.max(
            0,
            filteredMarkers.length - selectedGroupDocs.length,
          );
          const originRef = doc(db, originCollection, selectedBatchId);
          if (remainingCount === 0) {
            operations.push({ type: 'delete', ref: originRef });
          } else {
            operations.push({
              type: 'update',
              ref: originRef,
              data: { markerCount: remainingCount },
            });
          }
        }
      }

      // Add the new batch record to excelImports without templateSnapshot
      const importRecord = {
        filename: cleanFilename,
        importDate: new Date().toISOString(),
        markerCount: selectedGroupDocs.length,
        menuId: null,
      };
      operations.push({
        type: 'set',
        ref: doc(db, 'excelImports', newBatchId),
        data: importRecord,
      });

      // Commit in chunks of 400 to respect Firestore's 500-operation batch limit
      const chunkSize = 400;
      for (let i = 0; i < operations.length; i += chunkSize) {
        const chunk = operations.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        chunk.forEach((op) => {
          if (op.type === 'update') batch.update(op.ref, op.data);
          else if (op.type === 'setMerge') batch.set(op.ref, op.data, { merge: true });
          else if (op.type === 'set') batch.set(op.ref, op.data);
          else if (op.type === 'delete') batch.delete(op.ref);
        });
        await batch.commit();
      }

      // Also update window.__markerArray in-memory if present so duplicate checks stay in sync
      if (window.__markerArray && Array.isArray(window.__markerArray)) {
        const movedGroupIds = new Set(selectedMarkerIds);
        window.__markerArray.forEach((m) => {
          if (!m) return;
          const mgId = m.markerGroupID || m.data?.markerGroupID || m.id;
          if (movedGroupIds.has(mgId)) {
            m.importBatchId = newBatchId;
            if (!m.date || (typeof m.date === 'string' && m.date.trim() === '')) {
              m.date = todayDate;
            }
            if (m.data) {
              m.data.importBatchId = newBatchId;
              if (
                !m.data.date ||
                (typeof m.data.date === 'string' && m.data.date.trim() === '')
              ) {
                m.data.date = todayDate;
              }
            }
          }
        });
      }

      setSelectedMarkerIds([]);
      setAlertState({
        open: true,
        message: `Successfully created batch "${cleanFilename}" with ${selectedGroupDocs.length} marker(s).`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error creating batch in CheckBatches:', error);
      setAlertState({
        open: true,
        message: `Failed to create batch: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState((prev) => ({ ...prev, open: false }));
  };

  const isLoading = loadingActive || loadingArchived || loadingMarkers;

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {/* --- TOP TOOLBAR --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
              <MenuUploadGrey
                sx={{ fontSize: ICON_SIZE }}
                iconScale={PAGE1_UPLOAD_SCALE}
                iconX={PAGE1_UPLOAD_ICON_X}
                iconY={PAGE1_UPLOAD_ICON_Y}
                textSize={PAGE1_UPLOAD_TEXT_SIZE}
                textY={PAGE1_UPLOAD_TEXT_Y}
                label='Upload'
              />
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
              <MenuImportGrey
                sx={{ fontSize: ICON_SIZE }}
                iconScale={PAGE2_IMPORT_SCALE}
                iconX={PAGE2_IMPORT_ICON_X}
                iconY={PAGE2_IMPORT_ICON_Y}
                textSize={PAGE2_IMPORT_TEXT_SIZE}
                textY={PAGE2_IMPORT_TEXT_Y}
                label='Import'
              />
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={{ ...toolBlockStyle, position: 'relative' }}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled>
            <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* --- SECONDARY HEADER (FIRST SUBMENU: BATCH PULLDOWN & ICON) --- */}
      <Box
        sx={{
          height: '55px',
          minHeight: '55px',
          maxHeight: '55px',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f9f9f9',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 49,
          px: 2,
          py: 0.5,
          boxSizing: 'border-box',
        }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            pr: 2,
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              maxWidth: '520px',
            }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 'bold', color: 'text.secondary', whiteSpace: 'nowrap' }}>
              Batch:
            </Typography>
            <Select
              fullWidth
              size='small'
              displayEmpty
              value={selectedBatchId}
              onChange={handleBatchChange}
              sx={{
                height: '30px',
                backgroundColor: '#fff',
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: '0px',
                  minHeight: '30px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              MenuProps={{ style: { zIndex: 100020 } }}>
              <MenuItem value=''>
                <em style={{ color: '#999' }}>Select a batch...</em>
              </MenuItem>
              <MenuItem value='NONE' sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                None
              </MenuItem>
              {allBatches.map((batch) => {
                const dateStr = formatDateOnly(batch.importDate);
                const timeStr = formatTimeOnly(batch.importDate);
                const fileStr = batch.filename || 'Unknown';
                const label = `${dateStr} - ${timeStr} - ${fileStr}`;

                return (
                  <MenuItem key={batch.id} value={batch.id} sx={{ fontSize: '0.875rem' }}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </Box>

        <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

        <Box
          sx={{
            width: '110px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ml: 1,
          }}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(5px)' }}
            disabled={selectedMarkerIds.length === 0 || isLoading || isProcessing}
            onClick={handleCreateBatchClick}
            title='Create Batch'>
            {selectedMarkerIds.length > 0 && !isLoading && !isProcessing ? (
              <MenuCreateBatch sx={{ fontSize: ICON_SIZE * 1.5 }} />
            ) : (
              <MenuCreateBatchGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* --- MAIN CONTENT AREA (SECOND SUBMENU: MARKERS TABLE) --- */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <CheckBatchesView
          markers={filteredMarkers}
          categories={categories}
          regions={regions}
          isLoading={isLoading}
          selectedBatchId={selectedBatchId}
          selectedIds={selectedMarkerIds}
          setSelectedIds={setSelectedMarkerIds}
        />
      </Box>

      {/* --- FOOTER --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          position: 'relative',
        }}>
        <Box sx={{ minWidth: '150px', display: 'flex', alignItems: 'center' }}>
          {Boolean(selectedBatchId) && !isLoading && (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
              Total Markers: {filteredMarkers.length}
            </Typography>
          )}
        </Box>
      </Box>

      {/* --- STEP 1: BATCH FILENAME PROMPT DIALOG --- */}
      <ConfirmDialog
        open={showBatchFilenameDialog}
        onClose={() => setShowBatchFilenameDialog(false)}
        onConfirm={() => handleFilenameDialogConfirm(tempBatchFilename)}
        message={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography>Please enter the Batch Filename for this new batch:</Typography>
            <TextField
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Batch Filename'
              value={tempBatchFilename}
              onChange={(e) => setTempBatchFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleFilenameDialogConfirm(e.target.value);
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  textTransform: 'none !important',
                },
              }}
            />
          </Box>
        }
        severity='info'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      {/* --- STEP 2: CONFIRM DELETE FROM LIST & CREATE BATCH DIALOG --- */}
      <ConfirmDialog
        open={showCreateBatchConfirm}
        onClose={() => setShowCreateBatchConfirm(false)}
        onConfirm={executeCreateBatch}
        message='This will delete the selected rows from this list and create a new batch in Batch Import'
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alertState.open}
        onClose={handleCloseAlert}
        message={alertState.message}
        severity={alertState.severity}
        hideCancel={true}
        hideConfirm={true}
        autoClose={3000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
